import { Card } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  LineChart,
  Line,
} from "recharts";
import {
  AlertOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  BankOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { IntelligenceDto } from "dtos/application.dto";
import { AppCard } from "components/AppCard";

type OverViewProps = {
  data: IntelligenceDto;
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getRiskInfo = (score: number) => {
  if (score >= 8)
    return { level: "High Risk", color: "#ef4444", bg: "bg-red-50", text: "text-red-600", bar: "bg-red-500" };
  if (score >= 6)
    return {
      level: "Medium Risk",
      color: "#f97316",
      bg: "bg-orange-50",
      text: "text-orange-500",
      bar: "bg-orange-500",
    };
  if (score >= 4)
    return {
      level: "Low-Medium Risk",
      color: "#eab308",
      bg: "bg-yellow-50",
      text: "text-yellow-600",
      bar: "bg-yellow-500",
    };
  return { level: "Low Risk", color: "#22c55e", bg: "bg-green-50", text: "text-green-600", bar: "bg-green-500" };
};

const ProbabilityDefaultAnalysis = ({ data }: OverViewProps) => {
  if (!data.probability_of_default) return null;

  const pd = data.probability_of_default;
  const dti = data.debt_to_income_ratio;
  const riskInfo = getRiskInfo(pd.pd_score);

  const delinquencyData = [
    { name: "30 Days Past Due", value: pd.delinquency_history.thirty_days_past_due, color: "#f97316" },
    { name: "60 Days Past Due", value: pd.delinquency_history.sixty_days_past_due, color: "#ef4444" },
    { name: "Prior Defaults", value: pd.prior_defaults, color: "#dc2626" },
    { name: "Charge Offs", value: pd.charge_offs, color: "#7f1d1d" },
  ];

  const debtBreakdownData = Object.entries(dti.debt_breakdown)
    .filter(([, v]) => v > 0)
    .map(([name, value], i) => ({
      name,
      value,
      color: ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444"][i % 5],
    }));

  const dtiChartData = dti.monthly_dti.map((item) => ({
    label: `${MONTH_NAMES[item.month - 1]} ${item.year}`,
    dti: item.dti_percentage,
    debt: item.debt_payments,
  }));

  console.log(dtiChartData, "this is dtiChartData");

  const allClean = delinquencyData.every((d) => d.value === 0);

  return (
    <div className="p-4 space-y-6">
      <h1 className="font-bold text-2xl text-gray-800">Probability of Default Analysis</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <AppCard className="flex flex-col gap-3 border border-gray-300 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">PD Score</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${riskInfo.bg} ${riskInfo.text}`}>
              {riskInfo.level}
            </span>
          </div>
          <div className="flex items-end gap-1">
            <span className="text-3xl font-bold" style={{ color: riskInfo.color }}>
              {pd.pd_score}
            </span>
            <span className="text-sm text-gray-400 mb-1">/10</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all"
              style={{ width: `${pd.pd_score * 10}%`, backgroundColor: riskInfo.color }}
            />
          </div>
          <p className="text-xs text-gray-400">
            {pd.pd_score === 0
              ? "No default risk indicators detected in this period."
              : pd.pd_score < 4
                ? "Low likelihood of default based on current data."
                : "Elevated risk — review payment history carefully."}
          </p>
        </AppCard>

        {/* MCA Payments */}
        <AppCard className="flex flex-col gap-3 border border-gray-300 min-w-0">
          <div className="flex items-center gap-2">
            <DollarOutlined className="text-blue-500" />
            <span className="text-xs text-gray-400">MCA Payments</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">${pd.mca_payments_total.toLocaleString()}</div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{pd.total_mca_transactions} transactions</span>
            <span>{pd.mca_lenders.length} lenders</span>
          </div>
          <div className="text-xs text-gray-500 truncate">
            {pd.mca_lenders.length > 0
              ? pd.mca_lenders.slice(0, 2).join(", ") +
                (pd.mca_lenders.length > 2 ? ` +${pd.mca_lenders.length - 2} more` : "")
              : "No active MCA lenders"}
          </div>
        </AppCard>

        <AppCard className="flex flex-col gap-3 border border-gray-300 min-w-0">
          <div className="flex items-center gap-2">
            <BankOutlined className="text-purple-500" />
            <span className="text-xs text-gray-400">Debt Overview</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">${dti.total_outstanding_debt.toLocaleString()}</div>
          <div className="text-xs text-gray-400">Total Outstanding Debt</div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-xs">
            <div>
              <p className="text-gray-400">Monthly Obligations</p>
              <p className="font-semibold text-gray-700">${dti.monthly_debt_obligations.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400">Avg Monthly DTI</p>
              <p className="font-semibold text-gray-700">{dti.average_monthly_dti.toFixed(2)}%</p>
            </div>
          </div>
        </AppCard>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Delinquency History */}
        <Card title="Delinquency History" className="border border-gray-300">
          {allClean ? (
            <div className="flex flex-col items-center justify-center h-[220px] gap-2">
              <CheckCircleOutlined className="text-4xl text-green-500" />
              <p className="text-sm font-semibold text-green-600">Clean History</p>
              <p className="text-xs text-gray-400 text-center">No delinquencies, defaults, or charge-offs detected.</p>
            </div>
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={delinquencyData} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    angle={-35}
                    textAnchor="end"
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px" }}
                    formatter={(v) => [v, "Count"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {delinquencyData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Debt Breakdown" className="border border-gray-300">
          {debtBreakdownData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[220px] gap-2">
              <CheckCircleOutlined className="text-4xl text-green-500" />
              <p className="text-sm font-semibold text-green-600">No Active Debt</p>
              <p className="text-xs text-gray-400">No debt obligations found in this period.</p>
            </div>
          ) : (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={debtBreakdownData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <YAxis
                    tick={{ fill: "#6b7280", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${v.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px" }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, "Amount"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {debtBreakdownData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Monthly DTI Trend" className="border border-gray-300 lg:col-span-2">
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dtiChartData} margin={{ top: 5, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  yAxisId="dti"
                  tick={{ fill: "#3b82f6", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v}%`}
                />
                {/* <YAxis
                  yAxisId="debt"
                  orientation="right"
                  tick={{ fill: "#ef4444", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v.toLocaleString()}`}
                /> */}
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px" }}
                  formatter={(value: number, name: string) => [
                    name === "dti" ? `${value.toFixed(2)}%` : `$${value.toLocaleString()}`,
                    name === "dti" ? "DTI %" : "Debt Payments",
                  ]}
                />
                <Bar yAxisId="dti" dataKey="dti" fill="#3b82f6" name="dti" />
                {/* <Bar yAxisId="debt" dataKey="debt" fill="#ef4444" name="debt" /> */}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-blue-500 inline-block rounded-sm" />
              DTI %
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-red-400 inline-block rounded-sm" />
              Debt Payments
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ProbabilityDefaultAnalysis;
